import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, Loader2 } from "lucide-react";

function sectionFor(index) {
  if (index <= 50) return "Your outlook & temperament";
  if (index <= 90) return "How you engage with others";
  return "What quietly drives you";
}

export default function Assessment() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite") || undefined;
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [cur, setCur] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) { navigate("/start"); return; }
    api.questions().then((d) => setQuestions(d.questions)).catch(() => toast.error("Could not load questions"));
  }, [user, navigate]);

  const total = questions.length;
  const answered = Object.keys(answers).length;
  const q = questions[cur];
  const progress = total ? Math.round((answered / total) * 100) : 0;

  const pick = (value) => {
    const qid = q.id;
    setAnswers((a) => ({ ...a, [qid]: value }));
    setTimeout(() => { if (cur < total - 1) setCur((c) => c + 1); }, 180);
  };

  const submit = async () => {
    if (answered < total) { toast.error("Please answer every question first"); return; }
    setSubmitting(true);
    try {
      const res = await api.submit(answers, inviteToken);
      const asmId = res.assessment.id;
      if (res.pair) navigate(`/report/${asmId}`, { state: { pairId: res.pair.id } });
      else navigate(`/report/${asmId}`);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 402) { toast.error("A subscription is needed first"); navigate("/checkout"); }
      else toast.error(err?.response?.data?.detail || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  const options = useMemo(() => q?.options || [], [q]);
  if (!q) return <div className="max-w-xl mx-auto px-5 py-24 text-center text-[#918780]">Loading…</div>;

  const isLast = cur === total - 1;

  return (
    <div className="max-w-2xl mx-auto px-5 py-10">
      {/* progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-xs font-semibold text-[#918780] uppercase tracking-wider mb-2">
          <span>{sectionFor(q.index)}</span>
          <span data-testid="assessment-progress">{answered} / {total}</span>
        </div>
        <div className="h-2 rounded-full bg-[#E8E1D9] overflow-hidden">
          <div className="h-full bg-[#6B21A8] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={q.id}
          initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.28 }}
          className="bg-white border border-[#E8E1D9] rounded-2xl p-7 sm:p-9 shadow-sm"
        >
          <div className="text-sm font-semibold text-[#6B21A8] mb-3">Question {q.index}</div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#2B2625] leading-snug mb-7">
            {q.text}
          </h2>
          <div className="space-y-2.5">
            {options.map((o) => {
              const active = answers[q.id] === o.value;
              return (
                <button
                  key={o.value}
                  data-testid={`q-option-${o.value}`}
                  onClick={() => pick(o.value)}
                  className={`w-full text-left px-5 py-3.5 rounded-xl border transition-all font-medium ${
                    active
                      ? "border-[#6B21A8] bg-[#F4EAE1] text-[#2B2625] ring-2 ring-[#6B21A8]/20"
                      : "border-[#E8E1D9] bg-white text-[#6E655F] hover:border-[#6B21A8]/50 hover:bg-[#FBF6F0]"
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center justify-between mt-6">
        <button
          data-testid="assessment-back" onClick={() => setCur((c) => Math.max(0, c - 1))}
          disabled={cur === 0}
          className="inline-flex items-center gap-1.5 text-[#6E655F] hover:text-[#6B21A8] font-medium disabled:opacity-40"
        >
          <ChevronLeft size={18} /> Back
        </button>
        {(isLast || answered === total) && (
          <button
            data-testid="assessment-submit" onClick={submit} disabled={submitting}
            className="inline-flex items-center justify-center gap-2 bg-[#3F3A44] hover:bg-[#35313A] text-white font-semibold py-3 px-7 rounded-xl transition-colors active:scale-[0.98] disabled:opacity-60"
          >
            {submitting ? <><Loader2 className="animate-spin" size={18} /> Building your report…</> : "See my report"}
          </button>
        )}
      </div>
    </div>
  );
}
